/*

  Thermometer.ts

  Thermometer component for Gáta Dagsins progress tracking

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { ts } from "./i18n";
import { ComponentFunc, m } from "./mithril";
import { IPlayerMove, IRiddle } from "./types";
import { SunCorona } from "./suncorona";

// Constants for move display algorithm
const MIN_ZONE_HEIGHT_PERCENT = 15;

interface IZoneAllocation {
  hot: IPlayerMove[];
  warm: IPlayerMove[];
  cold: IPlayerMove[];
}

interface IZoneHeights {
  hot: number;
  warm: number;
  cold: number;
}

function removeBlankMarkers(word: string): string {
  // Remove question marks used to mark blank tiles for display purposes
  return word.replace(/\?/g, '');
}

function allocateMovesToZones(
  warmBoundary: number,
  hotBoundary: number,
  moves: IPlayerMove[],
): IZoneAllocation {

  const allocation: IZoneAllocation = {
    hot: [],
    warm: [],
    cold: []
  };
  
  moves.forEach(move => {
    if (move.score >= hotBoundary) {
      allocation.hot.push(move);
    } else if (move.score >= warmBoundary) {
      allocation.warm.push(move);
    } else {
      allocation.cold.push(move);
    }
  });
  
  return allocation;
}

function calculateZoneHeights(allocation: IZoneAllocation): IZoneHeights {
  const hotCount = allocation.hot.length;
  const warmCount = allocation.warm.length;
  const coldCount = allocation.cold.length;
  
  // Calculate base heights needed for moves (assuming 8% per move minimum)
  const moveHeightPercent = 8;
  const hotNeeded = Math.max(MIN_ZONE_HEIGHT_PERCENT, hotCount * moveHeightPercent);
  const warmNeeded = Math.max(MIN_ZONE_HEIGHT_PERCENT, warmCount * moveHeightPercent);
  const coldNeeded = Math.max(MIN_ZONE_HEIGHT_PERCENT, coldCount * moveHeightPercent);
  
  const totalNeeded = hotNeeded + warmNeeded + coldNeeded;
  
  // If total exceeds 100%, scale proportionally
  if (totalNeeded > 100) {
    const scale = 100 / totalNeeded;
    return {
      hot: hotNeeded * scale,
      warm: warmNeeded * scale,
      cold: coldNeeded * scale
    };
  }
  
  // Distribute remaining space proportionally
  const remaining = 100 - totalNeeded;
  const totalCounts = hotCount + warmCount + coldCount;
  const extraPerMove = totalCounts > 0 ? remaining / totalCounts : 0;
  const zeroFiller = totalCounts === 0 ? remaining / 3 : 0; // If no moves, distribute evenly
  
  return {
    hot: hotNeeded + (hotCount * extraPerMove) + zeroFiller,
    warm: warmNeeded + (warmCount * extraPerMove) + zeroFiller,
    cold: coldNeeded + (coldCount * extraPerMove) + zeroFiller,
  };
}

interface IBestScoreAttributes {
  score: number;
  // If defined, this is the best possible move, made by this player or another
  bestMove: IPlayerMove | undefined;
  onMoveClick: (word: string, coord: string) => void;
}

// Best possible score component (green circle at top)
export const BestPossibleScore: ComponentFunc<IBestScoreAttributes> = () => {
  return {
    view: (vnode) => {
      const { score, bestMove, onMoveClick } = vnode.attrs;
      // Determine the label based on achievement status
      let topLabel: string;
      if (bestMove !== undefined && bestMove.word) {
        // Current player achieved it - show their word
        topLabel = removeBlankMarkers(bestMove.word);
      } else {
        // Not achieved yet - show default label
        topLabel = ts("Besta mögulega lögn");
      }

      const celebrate = bestMove && bestMove.word !== "";

      return m(
        ".thermometer-best-score"
        + (celebrate ? ".celebrate" : ""),
        m(".thermometer-best-score-container", {
          onclick: () => celebrate && onMoveClick(bestMove.word, bestMove.coord)
        }, [
          // Sun corona behind the circle when celebrating
          celebrate ? m(SunCorona, { animate: true }) : null,
          m(".thermometer-best-circle", score.toString()),
          m(".thermometer-best-label", topLabel)
        ])
      );
    }
  };
};

interface IGlobalBestScoreAttributes {
  thisPlayer: boolean;
  score: number;
}

// Current global best score component (circled score)
export const GlobalBestScore: ComponentFunc<IGlobalBestScoreAttributes> = () => {
  return {
    view: (vnode) => {
      const { score, thisPlayer } = vnode.attrs;
      return m(".thermometer-current-score" + (thisPlayer ? ".this-player" : ""), [
        m(".thermometer-current-circle", score.toString())
      ]);
    }
  };
};

interface IPlayerMovesOverlayAttributes {
  moves: IPlayerMove[];
  maxScore: number;
  zoneHeights: IZoneHeights;
  zoneAllocation: IZoneAllocation;
  onMoveClick: (word: string, coord: string) => void;
}

// Player moves overlay wrapper component with zone-aware positioning
export const PlayerMovesOverlay: ComponentFunc<IPlayerMovesOverlayAttributes> = () => {
  return {
    view: (vnode) => {
      // Zones: "hot", "warm", "cold"
      // zoneHeights: How tall each zone is, as a percentage of the thermometer height
      // zoneAllocation: A dictionary with the list of moves for each zone
      const { zoneHeights, zoneAllocation, onMoveClick } = vnode.attrs;

      // Calculate zone boundaries
      const coldBottom = 0;
      const coldTop = zoneHeights.cold;
      const warmBottom = coldTop;
      const warmTop = warmBottom + zoneHeights.warm;
      const hotBottom = warmTop;

      const allMoveElements: m.Children[] = [];

      function scoreDetails(move: IPlayerMove) {
        if (move.isGlobalBestScore) {
          if (move.word === "") {
            // Another player holds the top score
            return [ m(GlobalBestScore, { thisPlayer: false, score: move.score }) ];
          } else {
            // This player is the holder of the top score
            return [
              m(GlobalBestScore, { thisPlayer: true, score: move.score }),
              m(".thermometer-move-word", removeBlankMarkers(move.word)),
              m(".thermometer-move-coord", `(${move.coord})`)
            ];
          }
        } else {
          return [
            m(".thermometer-move-score", move.score.toString()),
            m(".thermometer-move-word", removeBlankMarkers(move.word)),
            m(".thermometer-move-coord", `(${move.coord})`)
          ];
        }
      };

      function addZoneMoves(
        zoneMoves: IPlayerMove[],
        zoneName: string,
        zoneStart: number,
        zoneHeight: number,
      ) {
        const num = zoneMoves.length;
        const segmentHeight = zoneHeight / (num + 1);
        zoneMoves.forEach((move, index) => {
          // The zoneMoves list is in descending order, and we want
          // the first (highest) move to be at the top of the zone,
          // i.e. with the highest percentage position, which is
          // calculated from the bottom of the thermometer.
          const position = zoneStart + (segmentHeight * (num - index));
          allMoveElements.push(
            m(`.thermometer-move-overlay .${zoneName}`, {
              key: `${zoneName}-${index}`,
              style: `--move-position: ${position.toFixed(2)}%`,
              // Only call onMoveClick if the move was actually made
              // by the current player
              onclick: () => move.word && move.coord && onMoveClick(move.word, move.coord),
            }, scoreDetails(move))
          );
        });
      }

      addZoneMoves(zoneAllocation.cold, "cold", coldBottom, zoneHeights.cold);
      addZoneMoves(zoneAllocation.warm, "warm", warmBottom, zoneHeights.warm);
      addZoneMoves(zoneAllocation.hot, "hot", hotBottom, zoneHeights.hot);

      return m(".thermometer-moves-overlay-wrapper", allMoveElements);
    }
  };
};

interface IThermometerAttributes {
  riddle: IRiddle;
  selectedMoves: IPlayerMove[];
  bestMove: IPlayerMove | undefined;
  onMoveClick: (word: string, coord: string) => void;
}

// Main thermometer component with dynamic layout algorithm
export const Thermometer: ComponentFunc<IThermometerAttributes> = () => {
  return {
    view: (vnode) => {
      const { riddle, selectedMoves, bestMove, onMoveClick } = vnode.attrs;
      const { warmBoundary, hotBoundary, bestPossibleScore } = riddle;
      const zoneAllocation = allocateMovesToZones(
        warmBoundary,
        hotBoundary,
        selectedMoves,
      );
      const zoneHeights = calculateZoneHeights(zoneAllocation);

      return m(".thermometer-container", [
        // Main thermometer body with dynamic color zones and overlaid moves
        m(".thermometer-body", [
          // Dynamic color zones background
          m(".thermometer-zones", [
            m(".thermometer-zone.hot", {
              style: `--zone-size: ${zoneHeights.hot + zoneHeights.warm + zoneHeights.cold}%`
            }),
            m(".thermometer-zone.warm", {
              style: `--zone-size: ${zoneHeights.warm + zoneHeights.cold}%`
            }),
            m(".thermometer-zone.cold", {
              style: `--zone-size: ${zoneHeights.cold}%`
            })
          ]),

          // Player moves positioned using zone-aware algorithm
          m(PlayerMovesOverlay, {
            moves: selectedMoves,
            maxScore: bestPossibleScore,
            zoneHeights,
            zoneAllocation,
            onMoveClick,
          }),

        ]),
        // Best possible score at top
        m(BestPossibleScore, {
          score: bestPossibleScore,
          bestMove,
          onMoveClick,
        }),
      ]);
    }
  };
};
